import React from "react";
import './ProgramOfStudy.css';
import programBg from '../assets/background/Program.jpg';

const ProgramOfStudy = () => (
  <>
    {/* Hero Section */}
    <section className="program-hero">
      <div className="hero-background">
        <img src={programBg} alt="" />
      </div>
      <div className="hero-overlay"></div>
      <div className="container">
        <div className="content">
          <div className="hero-text">
            <div className="padding">
              <h1>Program of Study</h1>
              <h2>Academic Structure & Coursework</h2>
              <p>Our two-year program combines rigorous academic coursework with practical co-operative work experience, culminating in an independent research project that integrates theory and practice.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Program Schedule Section */}
    <section className="program-schedule">
      <div className="container">
        <div className="schedule-content">
          <div className="schedule-header">
            <h2>Program Schedule</h2>
            <p>Our program follows a structured timeline designed to maximize learning and practical experience:</p>
          </div>
          
          <div className="schedule-timeline">
            <div className="year-section">
              <h3>Year 1</h3>
              <div className="semester-grid">
                <div className="semester">
                  <h4>Fall Semester</h4>
                  <div className="course-codes">PSYC 6001, PSYC 6400</div>
                  <p>Core academic courses in research methods and psychological theory</p>
                </div>
                <div className="semester">
                  <h4>Winter Semester</h4>
                  <div className="course-codes">PSYC 6000, PSYC 6402</div>
                  <p>Core academic courses in advanced statistics and principles of group processes social psychology</p>
                </div>
                <div className="semester work-term">
                  <h4>Spring/Summer</h4>
                  <div className="course-codes"></div>
                  <p>First co-operative work placement</p>
                </div>
              </div>
            </div>
            
            <div className="year-section">
              <h3>Year 2</h3>
              <div className="semester-grid">
                <div className="semester">
                  <h4>Fall Semester</h4>
                  <div className="course-codes">PSYC 6403</div>
                  <p>Advanced coursework in program evaluation and applied psychology, practicum in applied research</p>
                </div>
                <div className="semester work-term">
                  <h4>Winter Semester</h4>
                  <div className="course-codes"></div>
                  <p>Second co-operative work placement</p>
                </div>
                <div className="semester">
                  <h4>Spring Semester</h4>
                  <div className="course-codes">PSYC 6404</div>
                  <p>Independent research project completion</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Course Descriptions Section */}
    <section className="course-descriptions">
      <div className="container">
        <div className="courses-content">
          <div className="courses-header">
            <h2>Course Descriptions</h2>
            <p>Our curriculum is designed to provide comprehensive training in applied psychological science:</p>
          </div>
          
          <div className="courses-grid">
            <div className="course-card">
              <div className="course-header">
                <h3>PSYC 6400</h3>
                <span className="course-credits">3 Credits</span>
              </div>
              <h4>Theory and Methods in Social Psychology</h4>
              <p>An overview of how theory and methods are used in social psychology to gain insight into social behaviour and how social perspectives are used to change behaviours and improve lives. In general, the course will provide students with a deeper understanding of social psychology and how our thoughts, feelings, and behaviours are influenced by our social context.</p>
            </div>
            
            <div className="course-card">
              <div className="course-header">
                <h3>PSYC 6001</h3>
                <span className="course-credits">3 Credits</span>
              </div>
              <h4>Research Design</h4>
              <p>This research methods and design course aims to develop critical thinking skills as consumers of psychological research, enabling students to evaluate and interpret research findings effectively. Throughout the course, students will gain comprehensive knowledge of research design procedures, including forming hypotheses, selecting appropriate designs and samples, developing or identifying measures, collecting data, analyzing results, and interpreting findings. They will also learn to identify and analyze various research designs found in the psychological literature, fostering a deeper understanding of the methods used in the field. Additionally, students will become aware of the tradeoffs that researchers must consider when conducting studies and publishing their work. Emphasizing the logic of the research design thought process, the course aims to equip students with the skills to approach research thoughtfully and effectively.</p>
            </div>
            
            <div className="course-card">
              <div className="course-header">
                <h3>PSYC 6402</h3>
                <span className="course-credits">3 Credits</span>
              </div>
              <h4>Principles of Group Processes</h4>
              <p>The purpose of this course is to provide students with an advanced understanding of the psychology of group processes. After completing it, students should be able to comprehend the major theories and research pertaining to group behaviour, and most importantly, learn how to apply scientific knowledge of group processes to real-world situations. The course will help prepare students to conduct research on group dynamics and navigate the complexities of the workplace. Throughout the course, there will be assessments covering topics such as social influence, power, leadership and psychological safety, productivity, teams, decision-making, conflict, inter-group relations, and crowds and collective behaviour. Additionally, there will be group activities that help translate theory into practice and to apply the psychological principles related to group behaviour in various contexts.</p>
            </div>
            
            <div className="course-card">
              <div className="course-header">
                <h3>PSYC 6000</h3>
                <span className="course-credits">3 Credits</span>
              </div>
              <h4>Advanced Statistics</h4>
              <p>This course introduces graduate students to statistical modeling through general and generalized linear models, with a strong emphasis on statistical thinking. Following the Guidelines for Assessment and Instruction in Statistics Education by the American Statistical Association, instruction prioritizes conceptual understanding, real data, and interpretation over mechanical computation.</p>
            </div>
            
            <div className="course-card">
              <div className="course-header">
                <h3>PSYC 6403</h3>
                <span className="course-credits">3 Credits</span>
              </div>
              <h4>Program Evaluation and Applied Research</h4>
              <p>This graduate seminar provides an in-depth introduction to the theory, methods, and practice of program evaluation and applied research. Students learn major evaluation approaches, research design options, qualitative and quantitative data-collection methods, and practical strategies for conducting evaluations in complex real-world settings. Students conduct evaluation/applied research for an agency pro bono, contributing to the research needs of the community and the university. The class acts as "consultants", so that students get a varied exposure to action research and evaluation. The emphasis is on field experience and critical engagement with the complexities of working in the field.</p>
            </div>
            
            <div className="course-card">
              <div className="course-header">
                <h3>PSYC 6404</h3>
                <span className="course-credits">3 Credits</span>
              </div>
              <h4>Project in Applied Psychological Science</h4>
              <p>This capstone course requires students to design, conduct, and report an independent research study that integrates their academic training with applied experience. Working under supervision, students develop a research question, design an appropriate methodology, analyze data—collected independently or sourced from existing datasets—and produce a professional APA-style research report. Class meetings focus on individualized guidance, methodological problem-solving, and progress check-ins. Through proposal development, progress reports, and completion of the final research project, students demonstrate their ability to apply psychological research skills to real-world or practice-based issues.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Work Term Section */}
    <section className="work-term">
      <div className="container">
        <div className="work-term-content">
          <div className="work-term-header">
            <h2>Co-operative Work Terms</h2>
            <p>Gain hands-on experience in real-world settings while earning competitive salaries</p>
          </div>
          
          <div className="work-term-info">
            <div className="work-term-overview">
              <h3>Work Term Overview</h3>
              <p>During each work term the student works for a participating employer and earns a competitive salary. The Co-op Co-ordinator organizes the competition for work term employment and oversees the application process.</p>
              
              <div className="work-term-features">
                <div className="feature">
                  <i className="fas fa-search"></i>
                  <h4>Placement Process</h4>
                  <p>Students may also obtain their own work term jobs outside the competition but the position must be approved by the Co-op Co-ordinator and the Program Chair.</p>
                </div>
                
                <div className="feature">
                  <i className="fas fa-handshake"></i>
                  <h4>Employer Selection</h4>
                  <p>Employers conduct interviews and select candidates. Placements are not guaranteed, but every effort is made to ensure that appropriate employment is made available.</p>
                </div>
                
                <div className="feature">
                  <i className="fas fa-chart-line"></i>
                  <h4>Success Rate</h4>
                  <p>In recent years, all students have obtained suitable placements with competitive salaries ranging from $6,000 to $7,500 per work term.</p>
                </div>
              </div>
            </div>
            
            <div className="work-term-evaluation">
              <h3>Work Term Evaluation</h3>
              <div className="evaluation-criteria">
                <div className="criterion">
                  <h4>1. Student Performance</h4>
                  <p>The student and employer jointly review the student's performance. The assessment is submitted to the Co-op Coordinator and to the Program Chair.</p>
                </div>
                
                <div className="criterion">
                  <h4>2. Work Term Report</h4>
                  <p>The report describes the work undertaken during the placement, including results from such work. Usually the report summarizes for the employer the activities/projects completed and the results of such work. The report is submitted to the Program Chair at the end of the work term.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Work Term Projects Section */}
    <section className="work-projects">
      <div className="container">
        <div className="projects-content">
          <div className="projects-header">
            <h2>Recent Work Term Reports</h2>
            <p>Our students have worked on diverse and impactful projects across various sectors:</p>
          </div>
          
          <div className="projects-grid">
            <div className="project-card">
              <h3>Government & Policy</h3>
              <ul>
                <li>Evaluation of the Enterprise Olympics Program, YMCA</li>
                <li>Evaluating the Arts, Department of Education and Early Childhood Development, Government of NL</li>
                <li>Strategic Planning for the Botanical Gardens, Office of Public Engagement, Memorial University</li>
                <li>R & D Vouchers program, Research Development Corporation</li>
              </ul>
            </div>
            
            <div className="project-card">
              <h3>Healthcare & Community</h3>
              <ul>
                <li>Project for Enhanced Rural & Remote Training, Primary Healthcare Research Unit</li>
                <li>Curriculum Program Review, Department of Education and Early Childhood Development, Government of NL</li>
                <li>Evaluation of Harm Reduction Workshop & Webinar, NL Housing & Homelessness Network</li>
                <li>Food Security Initiatives, Food First NL</li>
              </ul>
            </div>
            
            <div className="project-card">
              <h3>Social Services</h3>
              <ul>
                <li>Violence Prevention Initiative, Women's Policy Office, Government NL</li>
                <li>Community Health Assessment: Survey report</li>
                <li>Parkinson's Society NL Exercise Program evaluation</li>
                <li>Incorporating students in public engagement assessment</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Research Project Section */}
    <section className="research-project">
      <div className="container">
        <div className="research-content">
          <div className="research-header">
            <h2>The Research Project</h2>
            <p>Students in their second year carry out an independent research project that demonstrates their ability to conduct research and integrate theory, research, and practice.</p>
          </div>
          
          <div className="research-info">
            <div className="research-overview">
              <h3>Project Overview</h3>
              <p>The project is similar to a thesis in that it is an independent piece of research. It is different from a thesis because it is usually based on data from an applied setting, concerning a social problem or issue.</p>
              
              <p>The project will provide the "capstone" experience for students in the program. They will have the opportunity to produce a study integrating what they have learned in classes with what they have learned in their work terms.</p>
            </div>
            
            <div className="research-requirements">
              <h3>Project Requirements</h3>
              <ul>
                <li>Projects may involve an analysis of data collected during a work term or the study of an applied problem from a psychological perspective</li>
                <li>In all cases, the project involves the design of a study and the analysis of data</li>
                <li>The data, which the student may collect, which may be part of a data archive, or collected on a work term, forms an essential part of the project</li>
                <li>The research project must be separate from work term reports and cannot include material from the work term reports</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Example Projects Section */}
    <section className="example-projects">
      <div className="container">
        <div className="examples-content">
          <div className="examples-header">
            <h2>Prior Research Projects</h2>
            <p>Recent student research projects demonstrate the breadth and impact of our program:</p>
          </div>
          
          <div className="examples-grid">
            <div className="example-card">
              <h3>Healthcare & Medicine</h3>
              <ul>
                <li>Assessing pre-licensure collaborative skills using a simulated interprofessional team consultation exercise</li>
                <li>Exploring the relationship between positive mental health and contact with police among Canadians</li>
                <li>Immigrants' experiences accessing healthcare across provinces</li>
                <li>Perception of healthcare services provided by the Gathering Place</li>
              </ul>
            </div>
            
            <div className="example-card">
              <h3>Youth & Education</h3>
              <ul>
                <li>Youth engagement in Big Brothers Big Sisters of Canada: A jurisdictional scan of agency approaches and best practices</li>
                <li>No child left behind: Attitudes towards redistributing donations made to US public schools</li>
                <li>Police officer communication: Accommodation, trust and attitudes toward information provision</li>
              </ul>
            </div>
            
            <div className="example-card">
              <h3>Community & Social Impact</h3>
              <ul>
                <li>Eastern Health Community Health Assessment: Survey report</li>
                <li>Parkinson's Society NL Exercise Program evaluation</li>
                <li>Incorporating students in public engagement assessment: An exploratory data analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Call to Action */}
    <section className="program-cta">
      <div className="container">
        <div className="cta-content">
          <h2>Meet Our Faculty</h2>
          <p>Learn more about our dedicated faculty members who are committed to student success and excellence in applied psychological science.</p>
          <div className="cta-buttons">
            <a href="/our-people" className="btn secondary">Meet Our Faculty</a>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default ProgramOfStudy; 